import React, { useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { ArrowLeft } from 'lucide-react';
import SEO from '../components/SEO';
import { CONFIG } from '../config';
import { getStripe } from '../features/subscription/services/stripe';
import { createCheckoutSession } from '../features/subscription/services/subscriptionApi';

// Interval carried in the query string maps to the same plan identifier the old
// redirect flow sent (see RequireSubscription): annual -> 2, monthly -> 1.
const PRICE_ID_BY_INTERVAL = { annual: 2, monthly: 1 };

export default function SubscriptionCheckoutPage() {
    const [searchParams] = useSearchParams();
    const interval = searchParams.get('interval') === 'annual' ? 'annual' : 'monthly';
    const priceId = PRICE_ID_BY_INTERVAL[interval];

    // Auto-apply the promo only on the plan it's offered for — monthly today,
    // matching where the /subscribe page surfaces it. Undefined for annual (and
    // when no promo is configured), so the backend leaves the manual code field.
    const promoCode = interval === 'monthly'
        ? (process.env.REACT_APP_PROMO_CODE || undefined)
        : undefined;

    // Resolve Stripe only when this page mounts — never at module load. This
    // page is imported eagerly by App.js, so a module-scope getStripe() would
    // run loadStripe() on every route (and throw app-wide if the publishable
    // key is unset). getStripe() memoizes a single promise, so calling it here
    // still yields one stable instance for the whole app. Skip it entirely when
    // the key is missing so loadStripe('') never runs (it throws in Stripe.js).
    const stripePromise = CONFIG.STRIPE_PUBLISHABLE_KEY ? getStripe() : null;

    // Stripe calls this to obtain the session's client secret. The
    // {CHECKOUT_SESSION_ID} token is a literal string Stripe substitutes into
    // the returnUrl — we must not interpolate it ourselves.
    const fetchClientSecret = useCallback(async () => {
        const returnUrl = `${window.location.origin}/subscription/success?session_id={CHECKOUT_SESSION_ID}`;
        const { clientSecret } = await createCheckoutSession({ priceId, returnUrl, promoCode });
        return clientSecret;
    }, [priceId, promoCode]);

    return (
        <>
            <SEO
                title="Checkout"
                description="Complete your Graspd subscription."
                path="/subscription/checkout"
                noindex={true}
            />
            {/* Full-screen, standalone flow — App.js excludes this route from
                the app shell (see ConditionalLayout). Scrollable because
                Stripe's embedded form can run taller than the viewport. */}
            <div style={{
                position: 'fixed',
                inset: 0,
                overflowY: 'auto',
                background: 'var(--bg-primary)',
                boxSizing: 'border-box',
                padding: 'var(--space-6) var(--space-5) var(--space-7)',
            }}>
                <div style={{ width: '100%', maxWidth: 'var(--frame-prose)', margin: '0 auto' }}>
                    <Link
                        to="/subscribe"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            color: 'var(--text-secondary)',
                            fontSize: 'var(--text-body-size)',
                            fontWeight: 500,
                            textDecoration: 'none',
                            marginBottom: 'var(--space-5)',
                        }}
                    >
                        <ArrowLeft size={16} />
                        Back to plans
                    </Link>

                    {/* Stripe owns the entire payment UI inside its iframe,
                        including its own loading and error states — we render
                        no card fields or fallbacks of our own. The only thing
                        we handle is a missing publishable key (a build/config
                        error, not a payment error), which would otherwise throw
                        a cryptic Stripe.js exception. */}
                    {CONFIG.STRIPE_PUBLISHABLE_KEY ? (
                        <EmbeddedCheckoutProvider
                            stripe={stripePromise}
                            options={{ fetchClientSecret }}
                        >
                            <EmbeddedCheckout />
                        </EmbeddedCheckoutProvider>
                    ) : (
                        <p style={{
                            color: 'var(--text-secondary)',
                            fontSize: 'var(--text-body-size)',
                            lineHeight: 1.6,
                        }}>
                            Checkout isn’t configured in this environment
                            (missing <code>REACT_APP_STRIPE_PUBLISHABLE_KEY</code>).
                            Please try again later.
                        </p>
                    )}
                </div>
            </div>
        </>
    );
}
